import clsx from "clsx";
import {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";

import { ARIZONA_CYAN } from "@story/data-centers-hub/config/arizonaAssets";
import { ballastSlideSrc } from "@story/data-centers-hub/config/ballastAssets";
import { colors } from "@story/data-centers-hub/config/colors";
import { INTRO_WIPE_MS_NARROW } from "@story/data-centers-hub/config/intro";
import { LOUDOUN_YELLOW } from "@story/data-centers-hub/config/loudounAssets";
import { scheduleEnterFillPins } from "@story/data-centers-hub/config/scheduleEnterFillPins";
import type { DeckSlideId } from "@story/data-centers-hub/config/slides";
import { UTAH_ORANGE } from "@story/data-centers-hub/config/utahAssets";
import getBrowserInfo from "src/utils/get-browser-info";

import * as styles from "./ScrollBallast.css";

/** Match chapter `--hub-primary` (title / note use white chrome). */
const BALLAST_PRIMARY: Partial<Record<DeckSlideId, string>> = {
  note: colors.gridLine,
  title: colors.gridLine,
  virginia: LOUDOUN_YELLOW,
  utah: UTAH_ORANGE,
  arizona: ARIZONA_CYAN,
};

/** Deck order for alternating enter direction (even → from left). */
const BALLAST_ORDER: ReadonlyArray<DeckSlideId> = [
  "title",
  "virginia",
  "utah",
  "arizona",
];

function enterFromLeft(slideId: DeckSlideId): boolean {
  const id = slideId === "note" ? "title" : slideId;
  const index = BALLAST_ORDER.indexOf(id);
  return index < 0 || index % 2 === 0;
}

type Layer = {
  key: string;
  src: string;
  primary: string;
  fromLeft: boolean;
  open: boolean;
};

interface Props {
  /** Active chapter (not credits). */
  slideId: DeckSlideId;
}

/**
 * Fixed strip under the mosaic stage. Still switches like a chapter cell —
 * clip-path wipe (image parked), accent rule + traveling lead. Sibling of
 * the stage; not in the snap/document height.
 *
 * Android clip attrs are per layer (not on the shared root). A root-level
 * `data-hub-fill` clear at hop start was replaying enter on the under still
 * (closed pose → black flash) — same class of bug chapters fix with
 * hold/fill-lock on the outgoing root.
 */
const ScrollBallast: FunctionComponent<Props> = ({ slideId }) => {
  const src = ballastSlideSrc[slideId];
  const primary = BALLAST_PRIMARY[slideId] ?? colors.gridLine;
  const genRef = useRef(0);
  const [layers, setLayers] = useState<ReadonlyArray<Layer>>(() =>
    src
      ? [
          {
            key: `init-${slideId}`,
            src,
            primary,
            fromLeft: enterFromLeft(slideId),
            open: true,
          },
        ]
      : [],
  );

  /** Enter pins for the incoming layer only. */
  const [fillPin, setFillPin] = useState(true);
  const [fillLock, setFillLock] = useState(true);
  const [incomingKey, setIncomingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!src) {
      setLayers([]);
      setFillPin(true);
      setFillLock(true);
      setIncomingKey(null);
      return;
    }
    genRef.current += 1;
    const key = `${slideId}-${genRef.current}`;
    const fromLeft = enterFromLeft(slideId);
    setFillPin(false);
    setFillLock(false);
    setIncomingKey(key);
    setLayers((prev) => {
      const under = prev.filter((layer) => layer.open).slice(-1);
      return [...under, { key, src, primary, fromLeft, open: false }];
    });
    let inner = 0;
    let mid = 0;
    let cancelFillPins: (() => void) | undefined;
    // One extra frame vs chapter cells — starting the ballast wipe on the
    // same tick as active→hold clip handoff was a first-hop compositor hitch.
    const outer = requestAnimationFrame(() => {
      mid = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => {
          setLayers((prev) =>
            prev.map((layer) =>
              layer.key === key ? { ...layer, open: true } : layer,
            ),
          );
          cancelFillPins = scheduleEnterFillPins({
            wipeMs: INTRO_WIPE_MS_NARROW,
            setFillPin,
            setFillLock,
          });
        });
      });
    });
    const clear = window.setTimeout(() => {
      setLayers((prev) => prev.filter((layer) => layer.key === key));
      setIncomingKey(null);
    }, INTRO_WIPE_MS_NARROW);
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(mid);
      cancelAnimationFrame(inner);
      cancelFillPins?.();
      window.clearTimeout(clear);
    };
  }, [slideId, src, primary]);

  if (!src || layers.length === 0) {
    return null;
  }

  const android = Boolean(getBrowserInfo()?.android);

  return (
    <div className={styles.root} aria-hidden="true">
      {layers.map((layer) => {
        const isIncoming = layer.key === incomingKey;
        // Previous still stays painted under the incoming clip (chapter
        // underlay pattern). Muting it for the hop left the closed cell’s
        // black fill showing through on chapter→chapter.
        const isUnder = !isIncoming && layer.open && layers.length > 1;
        // Under: always lock (hop clears fillLock on the incoming path only).
        // Incoming/settled: lock once enter fill-lock latches.
        const lockOpen = isUnder || (layer.open && fillLock);
        return (
          <div
            key={layer.key}
            className={clsx(
              styles.shell,
              layer.fromLeft ? styles.leadFromLeft : styles.leadFromRight,
              layer.open &&
                (layer.fromLeft
                  ? styles.leadFromLeftOpen
                  : styles.leadFromRightOpen),
            )}
            style={{ ["--hub-primary" as string]: layer.primary }}
          >
            <div
              className={clsx(
                styles.cell,
                // Android keyframes keep from*+open; desktop swaps for transition.
                android
                  ? [
                      layer.fromLeft ? styles.fromLeft : styles.fromRight,
                      layer.open && styles.open,
                    ]
                  : layer.open
                    ? styles.open
                    : layer.fromLeft
                      ? styles.fromLeft
                      : styles.fromRight,
              )}
              data-hub-clip-anim={android ? "" : undefined}
              data-hub-hold={isUnder ? "" : undefined}
              data-hub-fill={
                isIncoming && layer.open && fillPin && !fillLock
                  ? ""
                  : undefined
              }
              data-hub-fill-lock={lockOpen ? "" : undefined}
            >
              <img
                className={styles.image}
                src={layer.src}
                alt=""
                decoding="async"
                loading="eager"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ScrollBallast;
